import { useEffect, useEffectEvent, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DOMPurify from 'dompurify';
import { BFF_BASE_URL } from '@apis/auth';
import type { IHelpGuideSummary } from '@apis/help';
import { ScreenshotLightbox } from '@components/ui/ScreenshotLightbox/ScreenshotLightbox';
import { GuideIconTile } from './GuideIconTile';
import { rebaseHelpAssets } from './helpFormat';
import styles from './GuideReaderPage.module.css';

interface IGuideBodyProps {
  html: string;
  /** Children of this guide when it is a parent; empty otherwise. */
  reports: IHelpGuideSummary[];
}

// Second pass: the server already sanitizes; this guards against a stale or
// tampered stored body. Screenshots are pointed at the API origin, and report
// names in tables become links to their guide. The markup object is memoised
// whole: React 19 compares `dangerouslySetInnerHTML` by identity, so a fresh
// `{ __html }` per render would rebuild the body on every re-render — dropping
// the search marks and the headings the scroll spy watches.
export function GuideBody({ html, reports }: IGuideBodyProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const ref = useRef<HTMLDivElement>(null);
  const [zoom, setZoom] = useState<string | null>(null);
  const markup = useMemo(() => {
    const doc = new DOMParser().parseFromString(DOMPurify.sanitize(html), 'text/html');
    rebaseHelpAssets(doc, BFF_BASE_URL);
    for (const strong of doc.querySelectorAll('td strong')) {
      const match = reports.find((report) => report.title.toLowerCase() === String(strong.textContent).trim().toLowerCase());
      if (match) strong.innerHTML = `<a class="${styles.xref}" data-xref href="/help/${match.slug}">${strong.innerHTML}</a>`;
    }
    return { __html: doc.body.innerHTML };
  }, [html, reports]);

  const onClick = useEffectEvent((e: MouseEvent) => {
    const target = e.target as HTMLElement;
    // Report-name links and the server's links to other guides stay in the app.
    const xref = target.closest('a[data-xref], a[href^="/help/"]');
    if (xref) {
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
      e.preventDefault();
      navigate(xref.getAttribute('href')!);
    } else if (target.tagName === 'IMG') {
      setZoom((target as HTMLImageElement).src);
    }
  });
  useEffect(() => {
    const element = ref.current!;
    element.addEventListener('click', onClick);
    return () => element.removeEventListener('click', onClick);
  }, []); // onClick is stable via useEffectEvent; subscribe once

  return (
    <>
      <div ref={ref} className={styles.doc} data-guide-body dangerouslySetInnerHTML={markup} />
      {reports.length > 0 ? (
        <>
          <h2 id="reports" className={styles.reportsHeading}>{t('help.reportGuidesHeading')}</h2>
          <div className={styles.reportGrid}>
            {reports.map((report) => (
              <Link key={report.slug} to={`/help/${report.slug}`} className={styles.reportCard} data-testid="report-card">
                <GuideIconTile iconKey={report.iconKey} variant="kid" />
                <span>
                  <b>{report.title}</b>
                  <span className={styles.reportSummary}>{report.summary}</span>
                  <small>{t('help.minRead', { count: report.minutes })}</small>
                </span>
              </Link>
            ))}
          </div>
        </>
      ) : null}
      {zoom ? (
        <ScreenshotLightbox src={zoom} onClose={() => setZoom(null)} />
      ) : null}
    </>
  );
}
